import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm4_u2_x1_co2';

const images = [
  { key: 'placeholder', src: '/assets/drawable/zzz_help.png', label: 'CO2-Regelung' }
];

const textBlocks = [
    { key: 'block_titel', heading: 'CO₂: Bedeutung, Messung und Arbeitsklima', body: '' },
    { key: 'block_was_ist', heading: 'Was ist CO₂?', body: 'CO₂ steht für Kohlenstoffdioxid (oft auch Kohlendioxid geschrieben). Es ist ein farb- und geruchloses Gas, das natürlicherweise in der Erdatmosphäre vorkommt und ein wichtiger Bestandteil des Kohlenstoffkreislaufs ist.' },
    { key: 'block_entstehung', heading: 'Entstehung von CO₂', body: '**Natürlich:** Durch die Atmung von Lebewesen (Menschen und Tiere atmen Sauerstoff ein und CO₂ aus), Verwesungsprozesse organischer Materialien, Vulkanausbrüche. Pflanzen nehmen CO₂ für die Fotosynthese auf.\n\n**Anthropogen (durch den Menschen verursacht):** Hauptsächlich durch die Verbrennung fossiler Brennstoffe (Kohle, Öl, Gas) zur Energiegewinnung, in industriellen Prozessen und durch Landnutzungsänderungen (z.B. Abholzung von Wäldern).' },
    { key: 'block_msr', heading: 'CO₂ und die MSR (Mess-, Steuer- und Regelungstechnik)', body: 'Im Kontext von Gebäuden und Arbeitsumgebungen spielt CO₂ eine wichtige Rolle für die Raumluftqualität und somit für die MSR-Systeme, die diese Qualität sicherstellen sollen.' },
    { key: 'block_indikator', heading: '1. Indikator für Luftqualität', body: 'In geschlossenen Räumen ist die Hauptquelle für den Anstieg der CO₂-Konzentration der Mensch selbst durch seine Atmung. Eine hohe CO₂-Konzentration deutet oft darauf hin, dass die Luft "verbraucht" ist und nicht ausreichend Frischluft zugeführt wird. Es korreliert häufig auch mit einer höheren Konzentration anderer Schadstoffe und Aerosole, die von Menschen ausgeatmet werden.' },
    { key: 'block_messen', heading: '2. Messen', body: 'CO₂-Sensoren sind ein wichtiger Bestandteil moderner Gebäudeautomationssysteme. Sie messen kontinuierlich die CO₂-Konzentration in der Raumluft.' },
    { key: 'block_steuern', heading: '3. Steuern und Regeln', body: '**Bedarfsgerechte Lüftung:** Die von den CO₂-Sensoren gemessenen Werte dienen als Eingangssignal für Lüftungsanlagen. Anstatt permanent oder zeitgesteuert zu lüften, kann die MSR die Lüftungsleistung bedarfsgerecht anpassen:\n• Steigt die CO₂-Konzentration über einen definierten Schwellenwert, erhöht die Lüftungsanlage den Außenluftanteil oder die Luftwechselrate.\n• Sinkt die CO₂-Konzentration wieder, kann die Lüftung reduziert werden.\n\n**Energieeffizienz:** Durch bedarfsgerechte Lüftung wird Energie gespart, da nicht unnötig viel Außenluft aufbereitet (geheizt oder gekühlt) werden muss.\n\n**Komfort und Gesundheit:** Die Regelung sorgt für eine konstant gute Luftqualität, was sich positiv auf das Wohlbefinden, die Konzentrationsfähigkeit und die Gesundheit der Personen im Raum auswirkt.' },
    { key: 'block_richtwerte', heading: 'Welche CO₂-Werte sind für ein gutes Arbeitsklima okay?', body: 'Die Bewertung von CO₂-Konzentrationen in Innenräumen erfolgt oft anhand von Richtwerten. Die Einheit ist typischerweise ppm (parts per million – Teile pro Million).\n\n**Außenluft:** Die CO₂-Konzentration in der Außenluft liegt global bei etwa 400-420 ppm und steigt tendenziell an.' },
    { key: 'block_innenraum', heading: 'Innenräume – Empfehlungen für ein gutes Arbeitsklima', body: '**< 800 ppm:** Sehr gute Raumluftqualität. Idealer Bereich für hohe Konzentration und Wohlbefinden.\n\n**800 - 1000 ppm:** Gute Raumluftqualität (Pettenkofer-Zahl). Zielbereich für Lüftungsregelungen. Keine negativen Auswirkungen zu erwarten.\n\n**1000 - 1400 ppm:** Akzeptable bis mäßige Raumluftqualität. Erste Beeinträchtigungen möglich (Müdigkeit, Konzentrationsschwächen). Häufiger lüften.\n\n**> 1400 ppm:** Schlechte Raumluftqualität. Deutliche Beeinträchtigungen (Kopfschmerzen, Müdigkeit). Lüftungsmaßnahmen dringend erforderlich. (Empfehlung UBA: < 2000 ppm).\n\n**> 5000 ppm:** Arbeitsplatzgrenzwert (AGW) für CO₂ in Deutschland (8h Exposition). In normalen Büros selten, aber in schlecht belüfteten, stark besetzten Räumen möglich.' },
    { key: 'block_fazit', heading: 'Zusammenfassung', body: 'CO₂ ist ein wichtiger Indikator für die Luftqualität in Innenräumen. Moderne MSR-Systeme nutzen CO₂-Sensoren, um Lüftungsanlagen bedarfsgerecht zu steuern. Dies verbessert nicht nur den Komfort und die Leistungsfähigkeit der Menschen, sondern trägt auch zur Energieeffizienz des Gebäudes bei. Für ein gutes Arbeitsklima sollte die CO₂-Konzentration idealerweise unter 1000 ppm, besser noch unter 800 ppm, gehalten werden.' }
];

const M4_U2_X1_Co2: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM4, canUploadImagesM4 } = useAuth();
  const canEditTexts = canEditTextsM4;
  const canUploadImages = canUploadImagesM4;
  const { darkMode } = useTheme();
  
  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(() => 
    textBlocks.map(tb => ({
      ...tb,
      body: override?.texts?.[tb.key] || tb.body
    })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (err) {
      console.error('Failed to update text:', err);
      alert('Speichern fehlgeschlagen.');
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Upload fehlgeschlagen.');
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    'aria-label': 'Text vorlesen',
    sx: { ml: 1 }
  }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(
    UnitPageShell,
    { title: '4.2.1 CO₂ – Bedeutung und Messung', actions: ttsBtn },
    React.createElement(
      Paper,
      { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(
        Box,
        {
          sx: {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
            gap: 3
          }
        },
        React.createElement(
          Box,
          { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          images.map(img =>
            React.createElement(ZoomableImage, {
              key: img.key,
              src: imgSrc(img.key, img.src),
              label: img.label,
              onReplace: canUploadImages ? (file: File) => handleImageReplace(img.key, file) : undefined
            })
          )
        ),
        React.createElement(
          Box,
          { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
          mergedBlocks.map(block =>
            React.createElement(EditableTextBlock, {
              key: block.key,
              heading: block.heading,
              body: block.body,
              onSave: canEditTexts ? (newBody: string) => handleTextSave(block.key, newBody) : undefined,
              pageId: PAGE_ID,
              blockId: block.key
            })
          )
        )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m4_u2_x99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M4_U2_X1_Co2;

